import { Injectable } from '@angular/core';
import { ObservationsRegistration } from 'src/app/performance/models/performance.model';

import { Cycle, PropagationCompany } from '../models/cycle.model';

@Injectable({
  providedIn: 'root',
})
export class CycleService {
  constructor() {}

  public getCycleStartDate(cycle: Cycle): Date {
    if (cycle.arrivals && cycle.arrivals.length > 0) {
      return cycle.arrivals
        .map((a) => new Date(a.arrivalDate))
        .sort((a, b) => (a >= b ? 1 : -1))[0];
    }
    return undefined;
  }

  public getAndValidateCycleState(
    cycle: Cycle,
    observations: ObservationsRegistration[],
    propagationCompanies: PropagationCompany[],
  ): { [key: string]: number } | Error {
    const state: { [key: string]: number } = {};

    [...cycle.arrivals]
      .sort((a, b) => (a.id > b.id ? 1 : -1))
      .forEach((arrival) => {
        arrival.allocations.forEach((allocation) => {
          const key = `${allocation.section.id}_${allocation.propagationCompany.id}`;
          if (state[key] === undefined) {
            state[key] = allocation.numberOfAnimals;
          } else {
            state[key] = state[key] + allocation.numberOfAnimals;
          }
        });
      });
    for (const movement of [...cycle.movements].sort((a, b) => (a.id > b.id ? 1 : -1))) {
      const fromKey = `${movement.fromSection.id}_${movement.fromPropagationCompany.id}`;
      const toKey = `${movement.toSection.id}_${movement.toPropagationCompany.id}`;

      if ((state[fromKey] ?? 0) >= movement.numberOfAnimals) {
        if (
          movement.fromSection.breed.id === movement.toSection.breed.id &&
          movement.fromSection.gender === movement.toSection.gender
        ) {
          state[fromKey] = (state[fromKey] ?? 0) - movement.numberOfAnimals;
          state[toKey] = (state[toKey] ?? 0) + movement.numberOfAnimals;
        } else {
          return Error(
            `Movement ${fromKey} -> ${toKey} is invalid. Difference between from- and to section in the breed or gender.`,
          );
        }
      } else {
        return Error(
          `Movement ${fromKey} -> ${toKey} is invalid. Movement number of animals is larger than available.`,
        );
      }
    }

    for (const departure of [...cycle.departures].sort((a, b) => (a.id > b.id ? 1 : -1))) {
      for (const allocation of departure.allocations) {
        const key = `${allocation.section.id}_${departure.propagationCompany.id}`;
        if (state[key] >= allocation.numberOfAnimals) {
          state[key] = state[key] - allocation.numberOfAnimals;
        } else {
          return Error(
            `Departure ${key} is invalid. Departure number of chicks is larger than available.`,
          );
        }
      }
    }

    if (observations) {
      observations.forEach((o) => {
        let mortality = 0;
        mortality += o.mortalityRegistration.death ?? 0;
        mortality += o.mortalityRegistration.cripple ?? 0;
        mortality += o.mortalityRegistration.sexError ?? 0;
        mortality += o.mortalityRegistration.selection ?? 0;
        mortality += o.mortalityRegistration.other ?? 0;
        mortality += o.mortalityRegistration.damages ?? 0;

        if (mortality > 0) {
          let key: string;

          for (const keyState in state) {
            if (Object.prototype.hasOwnProperty.call(state, keyState) && state[keyState] > 0) {
              const keySplitted = keyState.split('_');
              if (keySplitted.length === 2) {
                if (o.sectionId.toString() === keySplitted[0]) {
                  const correspondingPropagationCompany = propagationCompanies.find(
                    (p) => p.id.toString() === keySplitted[1],
                  );
                  key = `${o.sectionId}_${correspondingPropagationCompany?.id}`;
                }
              }
            }
          }
          if (state[key] !== undefined) {
            state[key] = state[key] - mortality;
          }
        }
      });
    }
    return state;
  }
}
